import { APIProvider } from "@vis.gl/react-google-maps";
import { Field, useFormikContext } from "formik";
import { useCallback, useState, type ReactNode } from "react";
import { Col, Row } from "react-bootstrap";
import { LocationMapEditor } from "./LocationMapEditor";
import { LocationPlaceSearch } from "./LocationPlaceSearch";
import { RequiredFieldMarker } from "../../Forms/RequiredFieldMarker";
import type { ILocationMapConfig } from "../../../customTypings/location/Location";
import { showToastWarning, shouldShowFormFieldError } from "../../../utils";
import type { LocationFormValues } from "../../../pages/LocationEditPage/LocationEditFormGrid";
import type { LocationSelectionResult } from "../../../utils/location/locationSelection";

export interface LocationMapAddressFieldsProps {
    mapConfig: ILocationMapConfig | null;
    mapsEnabled: boolean;
    metaDeleted: boolean;
    showNameField?: boolean;
    showLocationEnabledField?: boolean;
    isNew: boolean;
    liveValidationAfterSubmit: boolean;
    submitCount: number;
    /** Rendered beside the longitude field on md+ breakpoints (e.g. customer new-location main checkbox). */
    coordinatesRowExtra?: ReactNode;
}

const LocationMapAddressFields = ({
    mapConfig,
    mapsEnabled,
    metaDeleted,
    showNameField = true,
    showLocationEnabledField = true,
    isNew,
    liveValidationAfterSubmit,
    submitCount,
    coordinatesRowExtra,
}: LocationMapAddressFieldsProps) => {
    const { values, errors, touched, setFieldValue, setValues, handleChange } = useFormikContext<LocationFormValues>();
    const hasPersistedRecord = Boolean(values.id?.trim());
    const [searchDisplayValue, setSearchDisplayValue] = useState("");

    const showErr = (field: keyof LocationFormValues) =>
        shouldShowFormFieldError(
            errors,
            field as keyof typeof errors,
            hasPersistedRecord,
            submitCount,
            liveValidationAfterSubmit,
            touched
        );

    const applyLocationSelection = useCallback(
        (selection: LocationSelectionResult) => {
            void setValues((prev) => ({
                ...prev,
                addressLine1: selection.address.addressLine1,
                addressLine2: selection.address.addressLine2,
                addressLine3: selection.address.addressLine3,
                town: selection.address.town,
                county: selection.address.county,
                postcode: selection.address.postcode,
                country: selection.address.country,
                latitude: selection.latitude,
                longitude: selection.longitude,
            }));
            setSearchDisplayValue(selection.searchDisplayValue);
        },
        [setValues]
    );

    const handleCoordinatesChange = useCallback(
        (latitude: number, longitude: number) => {
            void setValues((prev) => ({
                ...prev,
                latitude,
                longitude,
            }));
        },
        [setValues]
    );

    const handleGeocodeFailed = useCallback(
        (latitude: number, longitude: number) => {
            void setValues((prev) => ({
                ...prev,
                latitude,
                longitude,
            }));
            showToastWarning(
                "We couldn't find an address for this pin. The map position was saved ? you can type the address below."
            );
        },
        [setValues]
    );

    const defaultCenter = {
        lat: mapConfig?.defaultCenterLatitude ?? 51.5074,
        lng: mapConfig?.defaultCenterLongitude ?? -0.1278,
    };

    return (
        <Row className="g-3">
            {showNameField && (
                <Col md={6}>
                    <label className="form-label" htmlFor="location-name">
                        Name{" "}
                        <RequiredFieldMarker />
                    </label>
                    <Field
                        id="location-name"
                        name="name"
                        className={`form-control${showErr("name") ? " is-invalid" : ""}`}
                        disabled={metaDeleted}
                        aria-required
                        aria-invalid={showErr("name")}
                        onChange={handleChange}
                    />
                    {showErr("name") && <div className="invalid-feedback d-block">{errors.name}</div>}
                </Col>
            )}

            {showLocationEnabledField && isNew && (
                <Col md={6} className="d-flex align-items-end">
                    <div className="form-check">
                        <Field
                            id="location-is-enabled"
                            name="isEnabled"
                            type="checkbox"
                            className="form-check-input"
                            disabled={metaDeleted}
                        />
                        <label className="form-check-label" htmlFor="location-is-enabled">
                            Available in lists
                        </label>
                    </div>
                </Col>
            )}

            {mapsEnabled && mapConfig?.browserApiKey && mapConfig.mapId && (
                <Col xs={12}>
                    <APIProvider apiKey={mapConfig.browserApiKey}>
                        <LocationPlaceSearch
                            countryRestrictions={mapConfig.countryRestrictions ?? []}
                            disabled={metaDeleted}
                            searchDisplayValue={searchDisplayValue}
                            onPlaceSelected={applyLocationSelection}
                        />
                        <div className="mt-3">
                            <LocationMapEditor
                                mapId={mapConfig.mapId}
                                defaultCenter={defaultCenter}
                                defaultZoom={mapConfig.defaultZoom}
                                defaultMapType={mapConfig.defaultMapType}
                                latitude={values.latitude}
                                longitude={values.longitude}
                                disabled={metaDeleted}
                                onCoordinatesChange={handleCoordinatesChange}
                                onSelectionComplete={applyLocationSelection}
                                onGeocodeFailed={handleGeocodeFailed}
                            />
                        </div>
                    </APIProvider>
                </Col>
            )}

            <Col md={6}>
                <label className="form-label" htmlFor="location-address-line-1">
                    Address line 1 <RequiredFieldMarker />
                </label>
                <Field
                    id="location-address-line-1"
                    name="addressLine1"
                    className={`form-control${showErr("addressLine1") ? " is-invalid" : ""}`}
                    disabled={metaDeleted}
                    aria-required
                    aria-invalid={showErr("addressLine1")}
                />
                {showErr("addressLine1") && (
                    <div className="invalid-feedback d-block">{errors.addressLine1}</div>
                )}
            </Col>
            <Col md={6}>
                <label className="form-label" htmlFor="location-address-line-2">
                    Address line 2
                </label>
                <Field
                    id="location-address-line-2"
                    name="addressLine2"
                    className="form-control"
                    disabled={metaDeleted}
                />
            </Col>
            <Col md={6}>
                <label className="form-label" htmlFor="location-address-line-3">
                    Address line 3
                </label>
                <Field
                    id="location-address-line-3"
                    name="addressLine3"
                    className="form-control"
                    disabled={metaDeleted}
                />
            </Col>
            <Col md={6}>
                <label className="form-label" htmlFor="location-town">
                    Town
                </label>
                <Field id="location-town" name="town" className="form-control" disabled={metaDeleted} />
            </Col>
            <Col md={6}>
                <label className="form-label" htmlFor="location-county">
                    County
                </label>
                <Field id="location-county" name="county" className="form-control" disabled={metaDeleted} />
            </Col>
            <Col md={6}>
                <label className="form-label" htmlFor="location-postcode">
                    Postcode
                </label>
                <Field
                    id="location-postcode"
                    name="postcode"
                    className="form-control text-uppercase"
                    disabled={metaDeleted}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        void setFieldValue("postcode", e.target.value.toUpperCase());
                    }}
                />
            </Col>
            <Col md={6}>
                <label className="form-label" htmlFor="location-country">
                    Country <RequiredFieldMarker />
                </label>
                <Field
                    id="location-country"
                    name="country"
                    className={`form-control${showErr("country") ? " is-invalid" : ""}`}
                    disabled={metaDeleted}
                    aria-required
                    aria-invalid={showErr("country")}
                />
                {showErr("country") && <div className="invalid-feedback d-block">{errors.country}</div>}
            </Col>

            <Col md={6}>
                <label className="form-label">
                    Latitude <RequiredFieldMarker />
                </label>
                <input
                    className={`form-control font-monospace${showErr("latitude") ? " is-invalid" : ""}`}
                    readOnly
                    disabled
                    value={values.latitude != null ? values.latitude.toFixed(6) : ""}
                    placeholder="Set using map or search"
                    aria-invalid={showErr("latitude")}
                />
                {showErr("latitude") && <div className="invalid-feedback d-block">{errors.latitude}</div>}
            </Col>
            {coordinatesRowExtra ? <div className="w-100" /> : null}
            <Col md={6}>
                <label className="form-label">
                    Longitude <RequiredFieldMarker />
                </label>
                <input
                    className={`form-control font-monospace${showErr("longitude") ? " is-invalid" : ""}`}
                    readOnly
                    disabled
                    value={values.longitude != null ? values.longitude.toFixed(6) : ""}
                    placeholder="Set using map or search"
                    aria-invalid={showErr("longitude")}
                />
                {showErr("longitude") && <div className="invalid-feedback d-block">{errors.longitude}</div>}
            </Col>
            {coordinatesRowExtra ? (
                <Col md={6} className="d-flex align-items-end">
                    {coordinatesRowExtra}
                </Col>
            ) : null}
        </Row>
    );
};

export { LocationMapAddressFields };
